/**
 * Deterministic fixed-step machinery for Rosalie's snout verbs.
 *
 * The system deliberately takes only plain positions and button states: the
 * world owns authored targets, while rendering consumes the perception seam.
 * That keeps an interaction replayable without Three, Rapier, or a scene graph.
 */

import { type PerceptionTarget } from '@/anim/types';

/** World-space position, deliberately independent of Three and Rapier. */
export interface Position {
  x: number;
  y: number;
  z: number;
}

export type VerbKind = 'nudge' | 'root' | 'observe';

/**
 * The interaction system owns only the plain geometry a world declared here.
 *
 * Kept structural instead of importing the story ledger's `Mark`: interaction
 * replays on its own in Node, and a type-only story dependency is still a
 * dependency somebody will eventually turn into a runtime one by accident.
 */
export interface InteractionMark {
  readonly kind: 'route' | 'timing' | 'door';
  readonly at: readonly { readonly x: number; readonly z: number }[];
  /** What it is, as a picture on her sheet. Mirrors the ledger's `MarkGlyph`. */
  readonly glyph?: 'door' | 'person' | 'pig' | 'hook' | 'book';
}

export interface Interactable {
  id: string;
  /** Where the snout aims. World space, metres. */
  at: Position;
  /** Which verbs this thing accepts. */
  verbs: readonly VerbKind[];
  /** How close she must be, metres, measured from her origin to `at`. */
  reach: number;
  /** Seconds of held input to complete `root`/`observe`. Ignored by `nudge`. */
  duration?: number;
  /** Whether completion is permanent. Default true. */
  once?: boolean;
  /** Disabled things are never candidates but KEEP their progress. Default true. */
  enabled?: boolean;
  /**
   * What this thing becomes on her map when an `observe` hold completes.
   * Absent means `{ kind: 'door', at: [the interactable's own `at`] }`.
   */
  mark?: InteractionMark;
  /** The picture for the default mark, when `mark` is absent (D-239). */
  mapGlyph?: NonNullable<InteractionMark['glyph']>;
}

/** All authored interaction feel values live together for tuning and replay. */
export const INTERACTION_TUNING = {
  // WHY: a verb firing away from Rosalie's snout feels like the game acted for the player.
  nudgeConeHalfAngle: 0.7,
  // WHY: watching is deliberately more forgiving than acting from the same place.
  observeConeHalfAngle: 1.2,
  // WHY: physical verbs need a close, legible handoff from intent to contact.
  reachSlack: 1.0,
  // WHY: watching is a distance verb while touching an object is not.
  observeReachSlack: 3.0,
  // WHY: one short lockout prevents a held key from reading as repeated impacts.
  nudgeCooldown: 0.45,
  // WHY: consumers scale this shared, unitless shove into their own physical units.
  nudgeImpulse: 1.4,
  // WHY: a hiccup in a held button costs a moment, not the whole attempt.
  progressDecay: 0.8,
  // WHY: the snout must not snap when its candidate changes, matching perception damping below.
  targetWeightRise: 8,
  // WHY: attention eases away from a lost target instead of vanishing between fixed steps.
  targetWeightFall: 4,
} as const;

export interface InteractionEvent {
  kind: VerbKind;
  id: string;
  /** Only on `nudge`. Horizontal, y === 0. */
  impulse?: Position;
}

export interface InteractionSnapshot {
  /** The thing `interact` would act on, or null. */
  actId: string | null;
  /** The thing `observe` would watch, or null. */
  observeId: string | null;
  /** 0..1 for whichever hold is currently live, else 0. */
  progress: number;
  /** Which verb `progress` belongs to, or null. */
  progressVerb: VerbKind | null;
}

export interface InteractionInput {
  interactDown: boolean;
  interactPressed: boolean;
  observeDown: boolean;
}

type Self = Position & { facing: number };

const EMPTY_SNAPSHOT: InteractionSnapshot = {
  actId: null,
  observeId: null,
  progress: 0,
  progressVerb: null,
};

function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value));
}

function hasVerb(interactable: Interactable, verb: VerbKind): boolean {
  return interactable.verbs.includes(verb);
}

function finitePosition(position: Position): boolean {
  return Number.isFinite(position.x) && Number.isFinite(position.y) && Number.isFinite(position.z);
}

function finiteSelf(self: Self): boolean {
  return finitePosition(self) && Number.isFinite(self.facing);
}

/**
 * Fails a malformed `Interactable` list at the point a world declares it,
 * instead of letting `setInteractables`'s `Map` construction silently
 * overwrite a duplicate id or letting `#candidate`/`#advanceProgress` degrade
 * into permanent no-ops for a bad number.
 *
 * `context` is prepended to every message so a thrown error names the beat or
 * caller that produced the list, not just the offending interactable id —
 * `setInteractables` itself has no beat identity to offer, so callers that
 * know one should pass it.
 *
 * Deliberately narrower than the runtime's own tolerance in one place: `reach`
 * of exactly zero is accepted, matching `#candidate`'s `reach < 0` rejection
 * and `interaction.spec.ts`'s "a coincident target yields a finite impulse"
 * case — a reach of zero is the declared, tested meaning of "she must be
 * standing exactly on it", not a mistake.
 */
export function validateInteractables(list: readonly Interactable[], context?: string): void {
  const prefix = context === undefined ? '' : `${context}: `;
  const seenAt = new Map<string, number>();

  list.forEach((interactable, index) => {
    const { id } = interactable;
    if (id === '') {
      throw new Error(`${prefix}interactable at index ${index} has an empty id`);
    }
    const firstIndex = seenAt.get(id);
    if (firstIndex !== undefined) {
      throw new Error(`${prefix}duplicate interactable id "${id}" at indices ${firstIndex} and ${index}`);
    }
    seenAt.set(id, index);

    if (!finitePosition(interactable.at)) {
      throw new Error(`${prefix}interactable "${id}" has a non-finite position`);
    }
    if (!Number.isFinite(interactable.reach) || interactable.reach < 0) {
      throw new Error(`${prefix}interactable "${id}" has an invalid reach (${interactable.reach})`);
    }
    if (interactable.verbs.length === 0) {
      throw new Error(`${prefix}interactable "${id}" declares no verbs`);
    }
    const { duration } = interactable;
    if (duration !== undefined && (!Number.isFinite(duration) || duration <= 0)) {
      throw new Error(`${prefix}interactable "${id}" has an invalid duration (${duration})`);
    }
  });
}

function bearingTo(self: Self, target: Position): number {
  const dx = target.x - self.x;
  const dz = target.z - self.z;
  const cos = Math.cos(self.facing);
  const sin = Math.sin(self.facing);
  return Math.atan2(dx * cos - dz * sin, dx * sin + dz * cos);
}

export class InteractionSystem {
  readonly #tuning: typeof INTERACTION_TUNING;
  #interactables = new Map<string, Interactable>();
  #progress = new Map<string, number>();
  #completed = new Set<string>();
  #nudgeCooldownLeft = 0;
  #snapshot: InteractionSnapshot = { ...EMPTY_SNAPSHOT };
  #perceptionAt: Position | null = null;
  #perceptionWeight = 0;

  constructor(tuning: typeof INTERACTION_TUNING = INTERACTION_TUNING) {
    this.#tuning = tuning;
  }

  /**
   * Replace the world's interactables without losing save-state progress.
   *
   * Validates before installing anything: an empty or duplicate id, a
   * non-finite position, or a bad reach/duration would otherwise only surface
   * later as a wrong candidate or a hold that never completes. `context`
   * (e.g. `beat 9 / part 1`) is optional so replay/test callers that have no
   * beat identity to offer are not forced to invent one.
   */
  setInteractables(list: readonly Interactable[], context?: string): void {
    validateInteractables(list, context);
    this.#interactables = new Map(list.map((interactable) => [interactable.id, interactable]));
  }

  /** Enable/disable one id without changing its in-progress hold. */
  setEnabled(id: string, enabled: boolean): void {
    const interactable = this.#interactables.get(id);
    if (interactable === undefined) return;
    this.#interactables.set(id, { ...interactable, enabled });
  }

  /**
   * Move one interactable, without disturbing its progress or its completion.
   *
   * Beat 2 introduces the first thing in the game that can be watched **while it
   * moves** — the worker walking his round — and `Interactable.at` is a static
   * position because until now everything was. The alternative was to rebuild the
   * whole list through `setInteractables` every fixed step, which allocates a Map
   * sixty times a second and, worse, makes "did this thing move" and "did the
   * world change what exists" the same call: a beat that re-declared its list
   * each tick would silently undo every `setEnabled` the cues had done.
   *
   * Deliberately a no-op for an unknown id, matching `setEnabled`. A director
   * that points at something the world has not placed yet should not throw on the
   * fixed step.
   */
  moveTo(id: string, at: Position): void {
    const interactable = this.#interactables.get(id);
    if (interactable === undefined || !finitePosition(at)) return;
    this.#interactables.set(id, { ...interactable, at: { ...at } });
  }

  /**
   * Where an interactable is right now, or null if the world has not placed it.
   *
   * The camera's `focus` needs this and cannot get it from the list the beat
   * declared: `moveTo` replaces the entry in this map and leaves the caller's
   * array holding the position the worker had when the beat started. A rig aimed
   * at the strip curtain while the man it is meant to be watching is at the far
   * trough is exactly the kind of "it works, it just points at nothing" bug that
   * survives a green suite.
   *
   * Copied out, because the map's entries are this system's state and a caller
   * that holds a live reference gets to move things without going through
   * `moveTo`.
   */
  positionOf(id: string): Position | null {
    const interactable = this.#interactables.get(id);
    return interactable === undefined ? null : { ...interactable.at };
  }

  /**
   * The map mark for an authored target, including the default for the Act 1
   * observations that predate the ledger. The returned copy prevents a map
   * consumer from moving a target through the interaction system's state.
   */
  markOf(id: string): InteractionMark | null {
    const interactable = this.#interactables.get(id);
    if (interactable === undefined) return null;
    const mark = interactable.mark;
    if (mark !== undefined) {
      return {
        kind: mark.kind,
        at: mark.at.map((point) => ({ x: point.x, z: point.z })),
        ...(mark.glyph === undefined ? {} : { glyph: mark.glyph }),
      };
    }
    return {
      kind: 'door',
      at: [{ x: interactable.at.x, z: interactable.at.z }],
      ...(interactable.mapGlyph === undefined ? {} : { glyph: interactable.mapGlyph }),
    };
  }

  update(dt: number, self: Self, input: InteractionInput): readonly InteractionEvent[] {
    const frameDt = Number.isFinite(dt) ? Math.max(0, dt) : 0;
    const validSelf = finiteSelf(self);
    const act = validSelf ? this.#candidate(self, 'act') : null;
    const observe = validSelf ? this.#candidate(self, 'observe') : null;
    const events: InteractionEvent[] = [];

    this.#nudgeCooldownLeft = Math.max(0, this.#nudgeCooldownLeft - frameDt);
    if (validSelf && input.interactPressed && act !== null && hasVerb(act, 'nudge') && this.#nudgeCooldownLeft === 0) {
      events.push({ kind: 'nudge', id: act.id, impulse: this.#nudgeImpulse(self, act.at) });
      this.#nudgeCooldownLeft = this.#tuning.nudgeCooldown;
    }

    const root = input.interactDown && act !== null && hasVerb(act, 'root') ? act : null;
    const watched = input.observeDown && observe !== null && hasVerb(observe, 'observe') ? observe : null;
    // Root wins when both buttons are held, and that is a decision rather than
    // an accident of `??` order. Acting is the committed verb: she has her snout
    // in something, and letting an idle `Q` steal the progress bar off it would
    // read as the game losing track of what she is doing. Watching is what she
    // does when her snout is free.
    const active = root ?? watched;
    const activeVerb: VerbKind | null = root !== null ? 'root' : watched !== null ? 'observe' : null;
    this.#advanceProgress(frameDt, active, activeVerb, events);

    this.#snapshot = {
      actId: act?.id ?? null,
      observeId: observe?.id ?? null,
      progress: active === null ? 0 : this.progressOf(active.id),
      progressVerb: activeVerb,
    };
    this.#updatePerception(frameDt, input, act, observe);
    return events;
  }

  get snapshot(): InteractionSnapshot {
    return { ...this.#snapshot };
  }

  /** Emits the currently eased target so the perception solver can remain presentation-only. */
  perceptionTarget(): PerceptionTarget | null {
    const at = this.#perceptionAt;
    if (at === null || this.#perceptionWeight < 0.01) return null;
    return { ...at, kind: 'interactable', weight: this.#perceptionWeight };
  }

  completed(id: string): boolean {
    return this.#completed.has(id);
  }

  progressOf(id: string): number {
    return this.#progress.get(id) ?? 0;
  }

  /** Test seam and save-round-trip seam. */
  markCompleted(id: string): void {
    this.#completed.add(id);
  }

  #candidate(self: Self, mode: 'act' | 'observe'): Interactable | null {
    const cone = mode === 'observe' ? this.#tuning.observeConeHalfAngle : this.#tuning.nudgeConeHalfAngle;
    const slack = mode === 'observe' ? this.#tuning.observeReachSlack : this.#tuning.reachSlack;
    const permitted = mode === 'observe'
      ? (interactable: Interactable): boolean => hasVerb(interactable, 'observe')
      : (interactable: Interactable): boolean => hasVerb(interactable, 'nudge') || hasVerb(interactable, 'root');
    let best: { interactable: Interactable; bearing: number; distance: number } | null = null;

    for (const interactable of this.#interactables.values()) {
      // Completion deliberately does NOT remove something from candidacy.
      //
      // The first version filtered `#completed` out here, which is a smaller
      // change than it looks: once she has rooted the straw, the straw stops
      // being a candidate at all, so her snout stops pointing at it and she can
      // never nudge it again either. Beat 1 needs exactly that — she roots the
      // straw once and afterwards nudges the flattened patch where her sibling
      // was, and gets nothing back. A finished verb has to leave the object in
      // the world.
      //
      // So completion blocks the completing VERB (see #advanceProgress) and
      // `setEnabled` is what removes a thing from the world.
      if (interactable.enabled === false || !permitted(interactable)) continue;
      if (!finitePosition(interactable.at) || !Number.isFinite(interactable.reach) || interactable.reach < 0) continue;
      const dx = interactable.at.x - self.x;
      const dz = interactable.at.z - self.z;
      const distance = Math.hypot(dx, dz);
      if (distance > interactable.reach * slack) continue;
      const bearing = bearingTo(self, interactable.at);
      if (Math.abs(bearing) > cone) continue;

      // The snout is the cursor: distance-first selection picks a nearby object
      // beside Rosalie over the one she is visibly pointing at.
      if (best === null
        || Math.abs(bearing) < Math.abs(best.bearing)
        || (Math.abs(bearing) === Math.abs(best.bearing) && distance < best.distance)) {
        best = { interactable, bearing, distance };
      }
    }
    return best?.interactable ?? null;
  }

  #nudgeImpulse(self: Self, at: Position): Position {
    const dx = at.x - self.x;
    const dz = at.z - self.z;
    const length = Math.hypot(dx, dz);
    if (length > 0) {
      return { x: dx / length * this.#tuning.nudgeImpulse, y: 0, z: dz / length * this.#tuning.nudgeImpulse };
    }
    return {
      x: Math.sin(self.facing) * this.#tuning.nudgeImpulse,
      y: 0,
      z: Math.cos(self.facing) * this.#tuning.nudgeImpulse,
    };
  }

  #advanceProgress(
    dt: number,
    active: Interactable | null,
    verb: 'root' | 'observe' | null,
    events: InteractionEvent[],
  ): void {
    for (const [id, progress] of this.#progress) {
      // A completed hold stays at 1. Decaying it left `progressOf` drifting to
      // zero while `completed` stayed true, so the two disagreed about the same
      // object — and anything drawing a progress ring from one and a done state
      // from the other would have shown a finished thing emptying itself.
      if (id !== active?.id && !this.#completed.has(id)) {
        this.#progress.set(id, clamp(progress - this.#tuning.progressDecay * dt, 0, 1));
      }
    }
    // A finished one-shot cannot be re-held. The candidate is still live (see
    // #candidate) — it simply no longer accumulates.
    if (active === null || verb === null || this.#completed.has(active.id)) return;

    const duration = active.duration;
    if (duration === undefined || !Number.isFinite(duration) || duration <= 0) return;
    const before = this.progressOf(active.id);
    const after = clamp(before + dt / duration, 0, 1);
    this.#progress.set(active.id, after);
    if (before < 1 && after === 1) {
      events.push({ kind: verb, id: active.id });
      if (active.once !== false) this.#completed.add(active.id);
    }
  }

  #updatePerception(
    dt: number,
    input: InteractionInput,
    act: Interactable | null,
    observe: Interactable | null,
  ): void {
    const desired = input.observeDown && observe !== null ? observe : act;
    if (desired !== null) this.#perceptionAt = { ...desired.at };
    const targetWeight = desired === null ? 0 : 1;
    const damping = targetWeight > this.#perceptionWeight
      ? this.#tuning.targetWeightRise
      : this.#tuning.targetWeightFall;
    const step = 1 - Math.exp(-damping * dt);
    this.#perceptionWeight += (targetWeight - this.#perceptionWeight) * step;
    if (this.#perceptionWeight < 0.01 && desired === null) this.#perceptionAt = null;
  }
}
